import { useMemo } from 'react';

/**
 * P1-WU06 — small muted footer that prints the current build's short
 * SHA, tag and build date. Wired into the splash / login pane so it's
 * visible on every public page without crowding the authed dashboard.
 *
 * Reads from the build-time-injected `__BUILD__` global declared in
 * src/types/global.d.ts. For local `vite dev` runs the values are
 * `'dev' / 'local' / <ISO>`; CI builds inject the real GitHub
 * commit + ref.
 */
export function BuildStamp({ className }: { className?: string }) {
  const label = useMemo(() => {
    // Defensive — if for whatever reason the global wasn't injected
    // (e.g. someone hot-swapped vite.config.ts and forgot to restart),
    // fall through to an unobtrusive placeholder rather than throw.
    const build = (globalThis as { __BUILD__?: typeof __BUILD__ }).__BUILD__;
    if (!build) {
      return 'build: unknown';
    }
    // "abc1234" or "abc1234-dirty" (AQUASTAR-371); legacy 'dev' passes through.
    const dirty = build.sha.endsWith('-dirty') ? '-dirty' : '';
    const shortSha = build.sha === 'dev' ? 'dev' : build.sha.slice(0, 7) + dirty;
    // Date-only — keeping the full ISO timestamp out of the UI noise.
    const date = build.builtAt.slice(0, 10);
    return `build ${shortSha} · ${build.tag} · ${date}`;
  }, []);

  return (
    <div
      className={className ?? 'text-center mt-4 small text-muted'}
      data-testid="build-stamp"
      style={{ opacity: 0.6, fontSize: '0.75rem' }}
    >
      {label}
    </div>
  );
}
